import { useState } from 'react';
import {
  ChevronRight,
  FileText,
  Folder,
  FolderPlus,
  Image as ImageIcon,
  File as FileIcon,
  LayoutGrid,
  List as ListIcon,
  Trash2,
} from 'lucide-react';
import type { FileKind, TreeFile, TreeFolder, TreeNode } from '../../lib/projectsApi';
import type { UploadTask } from '../../hooks/useUploadQueue';
import { previewTypeOf } from '../../lib/filePreview';
import { UploadProgressBar } from '../ui/UploadProgress';

// ---------------------------------------------------------------------------
// Shared folder view (CLE-248)
//
// Finder/Explorer-style file browser extracted from the Projects ResourcesTab so
// People, Clients and Suppliers reuse the exact same layout and behaviour:
// breadcrumb navigation, grid/list toggle, folder + file icons, click-to-open
// folders and click-to-preview files.
//
// Folder icons are coloured with the Firmio accent purple (#8B5CF6) instead of
// the previous yellow/amber so they match the application theme. This is the
// single source of truth for folder colours across every entity.
// ---------------------------------------------------------------------------

/** Firmio accent purple — the theme folder colour (replaces the old amber). */
export const FOLDER_COLOR = '#8B5CF6';
/** Slightly deeper blue-purple used for hovered/active folders. */
export const FOLDER_COLOR_STRONG = '#7C3AED';

function formatBytes(bytes: number | null): string {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let i = 0;
  while (value >= 1024 && i < units.length - 1) {
    value /= 1024;
    i += 1;
  }
  return `${value.toFixed(value >= 10 ? 0 : 1)} ${units[i]}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ro-RO', { dateStyle: 'medium' }).format(new Date(iso));
}

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('ro-RO', { style: 'currency', currency }).format(amount);
}

export function FileKindIcon({ kind, className }: { kind: FileKind; className?: string }) {
  if (kind === 'pdf') return <FileText className={className} />;
  if (kind === 'image') return <ImageIcon className={className} />;
  if (kind === 'doc') return <FileText className={className} />;
  return <FileIcon className={className} />;
}

const STATUS_LABELS: Record<string, string> = {
  DRAFT: 'Draft',
  SENT: 'Issued',
  PAID: 'Received / Paid',
  OVERDUE: 'Overdue',
  CANCELLED: 'Cancelled',
};

function DirectionBadge({ direction }: { direction: 'INCOME' | 'OUTCOME' }) {
  const isIncome = direction === 'INCOME';
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        isIncome ? 'bg-emerald-100 text-emerald-800' : 'bg-orange-100 text-orange-800'
      }`}
    >
      {isIncome ? 'Income' : 'Outcome'}
    </span>
  );
}

// Images show the picture itself; every other file its kind icon. A picture
// that fails to load (expired link, unsupported format) falls back to the icon.
function FileThumb({ file, size }: { file: TreeFile; size: 'lg' | 'sm' }) {
  const [failed, setFailed] = useState(false);
  const box = size === 'lg' ? 'h-16 w-16' : 'h-5 w-5';
  if (file.kind === 'image' && file.downloadUrl && !failed) {
    return (
      <img
        src={file.downloadUrl}
        alt=""
        loading="lazy"
        onError={() => setFailed(true)}
        className={`${box} shrink-0 rounded object-cover ${size === 'lg' ? 'shadow-sm ring-1 ring-gray-200' : ''}`}
      />
    );
  }
  return (
    <FileKindIcon
      kind={file.kind}
      className={`${size === 'lg' ? 'h-12 w-12' : 'h-5 w-5 shrink-0'} ${
        file.kind === 'pdf' ? 'text-red-500' : 'text-gray-400'
      }`}
    />
  );
}

// Inline "new folder" name field, opened from the toolbar button.
function NewFolderForm({
  onCreate,
  onDone,
}: {
  onCreate: (name: string) => Promise<void>;
  onDone: () => void;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (trimmed.includes('/')) {
      setError('Numele nu poate conține „/”.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onCreate(trimmed);
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Folderul nu a putut fi creat.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      className="mt-3 flex flex-wrap items-center gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <FolderPlus size={16} style={{ color: FOLDER_COLOR }} />
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onDone();
        }}
        maxLength={80}
        placeholder="Numele folderului"
        aria-label="Numele folderului"
        className="w-56 rounded-md border border-gray-300 px-2.5 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
      />
      <button
        type="submit"
        disabled={busy || !name.trim()}
        className="rounded-md bg-brand-600 px-3 py-1 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
      >
        Creează
      </button>
      <button
        type="button"
        onClick={onDone}
        className="rounded-md px-2 py-1 text-sm text-gray-600 hover:bg-gray-100"
      >
        Anulează
      </button>
      {error && <span className="w-full text-xs text-red-600">{error}</span>}
    </form>
  );
}

// --- Folder navigation helpers ---------------------------------------------

export function findFolderByPath(root: TreeFolder, path: string): TreeFolder | null {
  if (path === '') return root;
  const segments = path.split('/');
  let current: TreeFolder = root;
  for (const seg of segments) {
    const next = current.children.find(
      (c): c is TreeFolder => c.type === 'folder' && c.path.split('/').pop() === seg,
    );
    if (!next) return null;
    current = next;
  }
  return current;
}

function breadcrumbsFor(root: TreeFolder, path: string): TreeFolder[] {
  const crumbs: TreeFolder[] = [root];
  if (path === '') return crumbs;
  const segments = path.split('/');
  let current = root;
  for (const seg of segments) {
    const next = current.children.find(
      (c): c is TreeFolder => c.type === 'folder' && c.path.split('/').pop() === seg,
    );
    if (!next) break;
    crumbs.push(next);
    current = next;
  }
  return crumbs;
}

// ---------------------------------------------------------------------------
// Folder browser view
// ---------------------------------------------------------------------------

type FolderLayout = 'grid' | 'list';

export function FolderView({
  root,
  onPreview,
  onDelete,
  path: controlledPath,
  onPathChange,
  uploads = [],
  onCreateFolder,
  onDeleteFolder,
}: {
  root: TreeFolder;
  onPreview: (file: TreeFile) => void;
  /**
   * Optional delete action rendered as a trash button on file entries. The
   * caller decides eligibility per file (return undefined via not passing the
   * prop, or filter inside the handler); folders never get one.
   */
  onDelete?: (file: TreeFile) => void;
  /**
   * Optional controlled navigation: when provided, the parent owns the current
   * folder path (e.g. so uploads land in the open folder). Uncontrolled
   * callers keep the original internal-state behaviour.
   */
  path?: string;
  onPathChange?: (path: string) => void;
  /**
   * Files currently being uploaded into the open folder. They are drawn as
   * placeholder entries alongside the real ones — with the file's own name and
   * a progress bar — so the person sees where the file is going to land while
   * it is still on its way, instead of an unchanged folder and a spinner
   * somewhere above it.
   */
  uploads?: UploadTask[];
  /**
   * Create a subfolder (by name) inside the open folder. The caller passes it
   * only where subfolders are allowed, so the "Folder nou" button follows it.
   */
  onCreateFolder?: (name: string) => Promise<void>;
  /** Delete a user-created folder; offered only on empty `custom` folders. */
  onDeleteFolder?: (folder: TreeFolder) => void;
}) {
  const [internalPath, setInternalPath] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const path = controlledPath ?? internalPath;
  // Finder-style icon grid by default, with a row list as the alternative.
  const [layout, setLayout] = useState<FolderLayout>('grid');
  const current = findFolderByPath(root, path) ?? root;
  const crumbs = breadcrumbsFor(root, path);

  function navigate(p: string) {
    setInternalPath(p);
    onPathChange?.(p);
  }

  function openNode(node: TreeNode) {
    if (node.type === 'folder') {
      navigate(node.path);
    } else if (previewTypeOf(node) !== 'none') {
      onPreview(node);
    } else if (node.downloadUrl) {
      window.open(node.downloadUrl, '_blank', 'noopener');
    } else {
      onPreview(node);
    }
  }

  return (
    <div>
      {/* Toolbar: breadcrumbs + layout toggle */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <nav className="flex flex-wrap items-center gap-1 text-sm text-gray-600">
          {crumbs.map((crumb, i) => (
            <span key={crumb.path || 'root'} className="flex items-center gap-1">
              {i > 0 && <ChevronRight size={14} className="text-gray-400" />}
              <button
                type="button"
                onClick={() => navigate(crumb.path)}
                className={`rounded px-1.5 py-0.5 hover:bg-gray-100 ${
                  i === crumbs.length - 1 ? 'font-semibold text-gray-900' : 'text-blue-600'
                }`}
              >
                {crumb.name}
              </button>
            </span>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {onCreateFolder && (
            <button
              type="button"
              onClick={() => setCreatingFolder(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <FolderPlus size={15} style={{ color: FOLDER_COLOR }} /> Folder nou
            </button>
          )}
          <div className="inline-flex overflow-hidden rounded-md border border-gray-300">
            <button
              type="button"
              onClick={() => setLayout('grid')}
              aria-pressed={layout === 'grid'}
              title="Icon view"
              className={`inline-flex items-center px-2 py-1 ${
                layout === 'grid'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid size={15} />
            </button>
            <button
              type="button"
              onClick={() => setLayout('list')}
              aria-pressed={layout === 'list'}
              title="List view"
              className={`inline-flex items-center border-l border-gray-300 px-2 py-1 ${
                layout === 'list'
                  ? 'bg-brand-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-50'
              }`}
            >
              <ListIcon size={15} />
            </button>
          </div>
        </div>
      </div>

      {creatingFolder && onCreateFolder && (
        <NewFolderForm
          key={path}
          onCreate={onCreateFolder}
          onDone={() => setCreatingFolder(false)}
        />
      )}

      {/* Contents */}
      {current.children.length === 0 && uploads.length === 0 ? (
        <p className="mt-6 text-sm text-gray-500">This folder is empty.</p>
      ) : layout === 'grid' ? (
        <FolderGrid
          nodes={current.children}
          onOpen={openNode}
          onDelete={onDelete}
          onDeleteFolder={onDeleteFolder}
          uploads={uploads}
        />
      ) : (
        <FolderList
          nodes={current.children}
          onOpen={openNode}
          onDelete={onDelete}
          onDeleteFolder={onDeleteFolder}
          uploads={uploads}
        />
      )}
    </div>
  );
}

// Only folders someone created, and only while nothing is filed in them.
function isDeletableFolder(node: TreeNode): node is TreeFolder {
  return node.type === 'folder' && Boolean(node.custom) && node.fileCount === 0;
}

// Finder-style icon grid: large folder/file icons in a responsive grid. The
// optional delete trash is a SIBLING of the tile button (absolute-positioned)
// because nesting interactive elements is invalid HTML.
function FolderGrid({
  nodes,
  onOpen,
  onDelete,
  onDeleteFolder,
  uploads = [],
}: {
  nodes: TreeNode[];
  onOpen: (node: TreeNode) => void;
  onDelete?: (file: TreeFile) => void;
  onDeleteFolder?: (folder: TreeFolder) => void;
  uploads?: UploadTask[];
}) {
  return (
    <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
      {uploads.map((upload) => (
        <UploadTile key={upload.id} upload={upload} />
      ))}
      {nodes.map((node) => (
        <div key={node.type === 'folder' ? node.path : node.id} className="group relative">
          <button
            type="button"
            onClick={() => onOpen(node)}
            title={node.name}
            className="flex w-full flex-col items-center gap-2 rounded-lg border border-transparent p-3 text-center transition group-hover:border-gray-200 group-hover:bg-gray-50"
          >
            <div className="relative flex h-16 w-16 items-center justify-center">
              {node.type === 'folder' ? (
                <Folder
                  className="h-14 w-14 transition-colors group-hover:text-[#7C3AED]"
                  style={{ color: FOLDER_COLOR }}
                  fill="currentColor"
                  stroke="white"
                />
              ) : (
                <FileThumb file={node} size="lg" />
              )}
            </div>
            <span className="line-clamp-2 w-full break-words text-xs font-medium text-gray-800">
              {node.name}
            </span>
            <span className="text-[10px] text-gray-400">
              {node.type === 'folder'
                ? `${node.fileCount} ${node.fileCount === 1 ? 'file' : 'files'}`
                : node.invoice
                  ? formatMoney(node.invoice.amountTotal, node.invoice.currency)
                  : formatBytes(node.fileSize)}
            </span>
          </button>
          {onDeleteFolder && isDeletableFolder(node) && (
            <button
              type="button"
              onClick={() => onDeleteFolder(node)}
              aria-label={`Șterge folderul ${node.name}`}
              title="Șterge folderul gol"
              className="absolute right-1 top-1 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 touch:opacity-100"
            >
              <Trash2 size={14} />
            </button>
          )}
          {onDelete && node.type === 'file' && !node.invoice && (
            <button
              type="button"
              onClick={() => onDelete(node)}
              aria-label={`Delete ${node.name}`}
              title="Delete"
              className="absolute right-1 top-1 rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 touch:opacity-100"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}

// Row list: name, metadata, badges (the original detailed layout). As in the
// grid, the optional delete trash is an absolute-positioned sibling of the row
// button (nesting interactive elements is invalid HTML).
function FolderList({
  nodes,
  onOpen,
  onDelete,
  onDeleteFolder,
  uploads = [],
}: {
  nodes: TreeNode[];
  onOpen: (node: TreeNode) => void;
  onDelete?: (file: TreeFile) => void;
  onDeleteFolder?: (folder: TreeFolder) => void;
  uploads?: UploadTask[];
}) {
  return (
    <ul className="mt-4 divide-y divide-gray-100 rounded-lg border border-gray-200">
      {uploads.map((upload) => (
        <li key={upload.id} className="px-4 py-3">
          <UploadRowBody upload={upload} />
        </li>
      ))}
      {nodes.map((node) => {
        const deletableFolder = Boolean(onDeleteFolder) && isDeletableFolder(node);
        const deletable =
          (Boolean(onDelete) && node.type === 'file' && !node.invoice) || deletableFolder;
        return (
          <li key={node.type === 'folder' ? node.path : node.id} className="relative">
            <button
              type="button"
              onClick={() => onOpen(node)}
              className={`flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 ${
                deletable ? 'pr-12' : ''
              }`}
            >
              <div className="flex min-w-0 items-center gap-3">
                {node.type === 'folder' ? (
                  <Folder className="h-5 w-5 shrink-0" style={{ color: FOLDER_COLOR }} />
                ) : (
                  <FileThumb file={node} size="sm" />
                )}
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-gray-900">{node.name}</p>
                  {node.type === 'file' && (
                    <p className="truncate text-xs text-gray-500">
                      {node.invoice ? (
                        <>
                          {formatMoney(node.invoice.amountTotal, node.invoice.currency)} ·{' '}
                          {STATUS_LABELS[node.invoice.status] ?? node.invoice.status}
                        </>
                      ) : (
                        <>
                          {formatBytes(node.fileSize)} · {formatDate(node.createdAt)}
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {node.type === 'folder' ? (
                  <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                    {node.fileCount} {node.fileCount === 1 ? 'file' : 'files'}
                  </span>
                ) : (
                  node.invoice && <DirectionBadge direction={node.invoice.direction} />
                )}
                {node.type === 'folder' && <ChevronRight size={16} className="text-gray-400" />}
              </div>
            </button>
            {deletableFolder && (
              <button
                type="button"
                onClick={() => onDeleteFolder!(node as TreeFolder)}
                aria-label={`Șterge folderul ${node.name}`}
                title="Șterge folderul gol"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            )}
            {deletable && !deletableFolder && (
              <button
                type="button"
                onClick={() => onDelete!(node as TreeFile)}
                aria-label={`Delete ${node.name}`}
                title="Delete"
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
              >
                <Trash2 size={15} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ---------------------------------------------------------------------------
// In-flight upload placeholders
//
// Drawn in the folder the file is being uploaded into, in the same slot the
// finished file will occupy, so the transfer reads as "the file is arriving
// here" rather than "something is happening somewhere".
// ---------------------------------------------------------------------------

function UploadTile({ upload }: { upload: UploadTask }) {
  return (
    <div className="relative flex flex-col items-center gap-2 rounded-lg border border-dashed border-gray-300 bg-gray-50/60 p-3 text-center">
      <div className="relative flex h-16 w-16 items-center justify-center">
        <FileKindIcon kind={kindFromMime(null, upload.name)} className="h-12 w-12 text-gray-300" />
      </div>
      <span className="line-clamp-2 w-full break-words text-xs font-medium text-gray-500">
        {upload.name}
      </span>
      <UploadProgressBar className="w-full" percent={upload.percent} phase={upload.phase} />
    </div>
  );
}

function UploadRowBody({ upload }: { upload: UploadTask }) {
  return (
    <div className="flex items-center gap-3">
      <FileKindIcon
        kind={kindFromMime(null, upload.name)}
        className="h-5 w-5 shrink-0 text-gray-300"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-500">{upload.name}</p>
        <UploadProgressBar className="mt-1.5" percent={upload.percent} phase={upload.phase} />
      </div>
      <span className="shrink-0 text-xs tabular-nums text-gray-400">
        {upload.phase === 'processing' ? '…' : `${upload.percent}%`}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tree building helper — turn flat, category-tagged files into a folder tree so
// People/Clients/Suppliers can reuse the same browser as Projects.
// ---------------------------------------------------------------------------

export interface FlatFile {
  id: string;
  name: string;
  category: string;
  kind: FileKind;
  fileType: string | null;
  fileSize: number | null;
  createdAt: string | null;
  downloadUrl: string | null;
}

export function kindFromMime(mime: string | null, fileName: string): FileKind {
  const ext = (fileName.split('.').pop() ?? '').toLowerCase();
  if ((mime ?? '').includes('pdf') || ext === 'pdf') return 'pdf';
  if ((mime ?? '').startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
    return 'image';
  }
  if (['doc', 'docx', 'txt', 'md', 'rtf', 'odt'].includes(ext)) return 'doc';
  return 'file';
}

/**
 * Build a single-level folder tree from flat, category-tagged files. Each
 * distinct category becomes a top-level folder; files sit inside their folder.
 */
export function buildTreeFromFiles(
  files: FlatFile[],
  options: { rootName: string; categoryOrder?: string[]; includeEmpty?: boolean },
): TreeFolder {
  const byCategory = new Map<string, TreeFile[]>();
  for (const cat of options.categoryOrder ?? []) byCategory.set(cat, []);
  for (const f of files) {
    const list = byCategory.get(f.category) ?? [];
    list.push({
      type: 'file',
      id: f.id,
      name: f.name,
      kind: f.kind,
      fileType: f.fileType,
      fileSize: f.fileSize,
      createdAt: f.createdAt,
      downloadUrl: f.downloadUrl,
      category: f.category,
    });
    byCategory.set(f.category, list);
  }

  const children: TreeNode[] = [];
  for (const [category, catFiles] of byCategory) {
    if (catFiles.length === 0 && !options.includeEmpty) continue;
    children.push({
      type: 'folder',
      path: category,
      name: category,
      children: catFiles,
      fileCount: catFiles.length,
    });
  }

  return {
    type: 'folder',
    path: '',
    name: options.rootName,
    children,
    fileCount: files.length,
  };
}
